import React, { useRef, useState } from 'react';

import { useQuery } from 'react-query';
import { Toast } from 'src/common/components';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import { fetchDidRoutingList, deleteDidRouting } from '../api';
import type { DidRoutingListResponse, DidRouting as DidRoutingType } from '../api/didRouting';
import { DidRoutingList, DidRoutingModal, DeleteConfirmModal } from '../components';
import type { DidRoutingModalRef } from '../components';

export const DidRouting: React.FC = () => {
  const modalRef = useRef<DidRoutingModalRef>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<DidRoutingType | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { data, isLoading, refetch } = useQuery<DidRoutingListResponse, Error>(
    ['did-routing', searchQuery],
    () => fetchDidRoutingList(1, 100, searchQuery || undefined),
    {
      refetchOnWindowFocus: false,
      retry: 1,
      onError: () => {
        Toast.error('Error al cargar las rutas DID');
      },
    }
  );

  const routes = data?.routes ?? [];

  const handleEdit = (route: DidRoutingType): void => {
    modalRef.current?.showEdit(route);
  };

  const handleDeleteRequest = (route: DidRoutingType): void => {
    setDeleteTarget(route);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!deleteTarget) return;

    setIsDeleting(true);
    try {
      await deleteDidRouting(deleteTarget.id);
      Toast.success('Ruta DID eliminada exitosamente');
      setDeleteTarget(null);
      refetch();
    } catch (err) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      const detail = axiosErr?.response?.data?.detail;
      Toast.error(typeof detail === 'string' ? detail : 'Error al eliminar la ruta DID');
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="flex flex-col h-full">
      <div className="border-b border-white-2.0 bg-primary-2.0 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-lg font-bold text-white-0.0">DID Routing</h1>
              {isLoading ? (
                <p className="text-xs text-white-1.0">Cargando rutas…</p>
              ) : (
                <p className="text-xs text-white-1.0">
                  {routes.length} {routes.length === 1 ? 'ruta' : 'rutas'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Icon
                name={OrbitIcons.Search}
                size={14}
                className="absolute left-2.5 top-1/2 -translate-y-1/2 text-white-0.0/50"
              />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Buscar DID..."
                className="pl-8 pr-3 py-1.5 bg-white/10 border border-white/20 rounded-md text-xs text-white-0.0 placeholder:text-white-0.0/50 focus:outline-none focus:ring-2 focus:ring-white/30"
              />
            </div>

            <button
              type="button"
              onClick={() => refetch()}
              className="flex items-center gap-1 rounded-md bg-white/10 px-3 py-1.5 text-xs text-white-0.0 hover:bg-white/20"
              title="Actualizar lista"
            >
              <Icon name={OrbitIcons.RefreshCw} size={14} />
              Actualizar
            </button>

            <button
              type="button"
              onClick={() => modalRef.current?.showCreate()}
              className="btn btn-secondary flex items-center gap-2"
              title="Crear nueva ruta DID"
            >
              <Icon name={OrbitIcons.PlusCircle} size={16} />
              Nueva Ruta
            </button>
          </div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-hidden">
        <DidRoutingList
          routes={routes}
          isLoading={isLoading}
          onEdit={handleEdit}
          onDelete={handleDeleteRequest}
        />
      </div>

      <DidRoutingModal ref={modalRef} onSaved={() => refetch()} />

      <DeleteConfirmModal
        isOpen={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        title="Eliminar Ruta DID"
        message={`¿Estás seguro de eliminar la ruta para ${deleteTarget?.did}? Esta acción no se puede deshacer.`}
        isDeleting={isDeleting}
      />
    </div>
  );
};
